import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Modal,
  TextField,
  Typography,
  MenuItem,
  Avatar,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  API_RESPONSE_CODE,
  BRAND_COLOR,
  BRAND_HOVER
} from "@kivunova/kivufrontendcommon";
import { Employee, EmployeeFormData } from "./types/employees";
import { UserRole } from "../Profile/enums/profileEnums";
import { useCreateEmployee } from "../../hooks/api/useEmployee";
import { useUploadProfileImage } from "../../hooks/api/useProfile";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import {
  PhoneCountryCodeMap,
  combinePhoneNumber,
  getDefaultCountryCode,
  useKivunovaTranslation,
  usePlatformConfig
} from "@kivunova/kivufrontendcommon";
import { formatUserRoleLabel } from "../Profile/utils/userRoleLabels";
import { useGetCompanyStores } from "../../hooks/api/useStore";
import {
  AlertBannerCodeMap,
  ALERT_BANNER_CODE
} from "@kivunova/kivufrontendcommon";
import { useParams } from "react-router-dom";
import {
  buildEmployeeSchema,
  getAssignableRoles,
  buildDefaults
} from "./utils/employeeUtil";

interface AddEmployeeModalProps {
  open: boolean;
  onClose: () => void;
}

const AddEmployeeModal: React.FC<AddEmployeeModalProps> = ({
  open,
  onClose
}) => {
  const { t } = useKivunovaTranslation();
  const { config } = usePlatformConfig();
  const createEmployeeMutation = useCreateEmployee();
  const useGetCompanyStoresMutation = useGetCompanyStores();
  const uploadProfileImage = useUploadProfileImage();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const storeList = useSelector((state: RootState) => state.store.storeList);
  const isStoreAdmin = userDetails?.role === UserRole.STORE_ADMIN;

  const [previewImage, setPreviewImage] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [apiError, setApiError] = useState<string>("");

  const { storeId: storeIdParams } = useParams<{ storeId: string }>();
  const storeId = isStoreAdmin ? userDetails?.storeId : storeIdParams;

  const assignableRoles = useMemo(
    () => getAssignableRoles(userDetails?.role as UserRole | undefined),
    [userDetails?.role]
  );

  const employeeSchema = useMemo(
    () => buildEmployeeSchema(assignableRoles, t),
    [assignableRoles, t]
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
    reset,
    watch,
    setValue
  } = useForm<EmployeeFormData>({
    resolver: yupResolver(employeeSchema),
    mode: "onChange",
    defaultValues: buildDefaults(userDetails as Employee, storeId)
  });

  const firstName = watch("firstName");
  const lastName = watch("lastName");

  const isDirtyRef = useRef(isDirty);
  useEffect(() => {
    isDirtyRef.current = isDirty;
  });

  useEffect(() => {
    if (isDirtyRef.current) return;
    if (!userDetails) return;

    reset(buildDefaults(userDetails, storeId), { keepDirty: false });
  }, [userDetails, storeId]);

  useEffect(() => {
    if (storeId && storeList.length > 0) {
      const exists =
        storeList.some((s) => s.id === storeId) || (storeId && isStoreAdmin);

      if (exists) {
        setValue("storeId", storeId, {
          shouldValidate: true,
          shouldDirty: false
        });
      }
    }
  }, [storeId, storeList, setValue, isStoreAdmin]);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const getInitials = () => {
    const first = firstName?.[0] || "";
    const last = lastName?.[0] || "";
    return `${first}${last}`.toUpperCase();
  };

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const onSubmit: SubmitHandler<EmployeeFormData> = async (data) => {
    try {
      setApiError("");

      let imageUrl = data.profileImage;

      if (selectedFile) {
        try {
          const uploadResult =
            await uploadProfileImage.mutateAsync(selectedFile);
          if (uploadResult?.uploadProfileImage?.code === API_RESPONSE_CODE.OK) {
            imageUrl = uploadResult.uploadProfileImage?.user?.profileImage;
          } else {
            console.error("❌ Failed to upload profile image");
          }
        } catch (uploadError) {
          console.error("❌ Image upload error:", uploadError);
        }
      }

      const fullPhoneNumber = combinePhoneNumber(
        data.phoneCountryCode as string,
        data.phone,
        PhoneCountryCodeMap
      );

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { phoneCountryCode, ...employeeData } = data;

      const result = await createEmployeeMutation.mutateAsync({
        ...employeeData,
        phone: fullPhoneNumber,
        profileImage: imageUrl
      });

      if (result?.createUser?.code && result.createUser.code !== "OK") {
        const errorCode = result.createUser.code as ALERT_BANNER_CODE;
        const errorMessage =
          AlertBannerCodeMap[errorCode]?.value ||
          t(
            "create-employee-failed",
            "Failed to create employee. Please try again."
          );
        setApiError(errorMessage);
        return;
      }

      reset(buildDefaults(userDetails as Employee, storeId));
      setPreviewImage("");
      setSelectedFile(null);
      setApiError("");
      onClose();
    } catch (err: any) {
      console.error("❌ Failed to create employee:", err);

      const errorCode = err?.response?.createUser?.code || err?.code;
      if (errorCode && AlertBannerCodeMap[errorCode as ALERT_BANNER_CODE]) {
        setApiError(AlertBannerCodeMap[errorCode as ALERT_BANNER_CODE].value);
      } else {
        setApiError(
          t(
            "create-employee-failed",
            "Failed to create employee. Please try again."
          )
        );
      }
    }
  };

  useEffect(() => {
    const companyId = userDetails?.companyId;
    (async () => {
      try {
        if (companyId && !isStoreAdmin) {
          await useGetCompanyStoresMutation.mutateAsync({ companyId });
        }
      } catch (e) {
        console.error("Failed to get employee list", e);
      }
    })();
  }, [userDetails?.companyId, isStoreAdmin]);

  useEffect(() => {
    if (!open) {
      setApiError("");
    }
  }, [open]);

  const isUploading = uploadProfileImage.isPending;
  const isLoading = isSubmitting || isUploading;

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="modal-styles">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 3,
            pb: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0
          }}
        >
          <Typography variant="h5" fontWeight="bold" color="textPrimary">
            {t("add-new-employee", "Add New Employee")}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            flexGrow: 1
          }}
        >
          {apiError && (
            <Alert
              severity="error"
              onClose={() => setApiError("")}
              sx={{ mb: 0 }}
            >
              {apiError}
            </Alert>
          )}

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 2
            }}
          >
            <Box sx={{ position: "relative", mb: 1 }}>
              <Avatar
                src={previewImage}
                alt={t("employee-alt", "Employee")}
                sx={{
                  width: 100,
                  height: 100,
                  fontSize: "2rem",
                  bgcolor: BRAND_COLOR,
                  border: `4px solid ${BRAND_COLOR}20`,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                }}
              >
                {!previewImage && getInitials()}
              </Avatar>

              {isUploading && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(0,0,0,0.5)",
                    borderRadius: "50%"
                  }}
                >
                  <CircularProgress size={30} sx={{ color: "white" }} />
                </Box>
              )}

              <input
                accept="image/*"
                style={{ display: "none" }}
                id="employee-image-upload"
                type="file"
                onChange={handleImageChange}
                disabled={isLoading}
              />
              <label htmlFor="employee-image-upload">
                <IconButton
                  component="span"
                  disabled={isLoading}
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    bgcolor: BRAND_COLOR,
                    color: "white",
                    width: 36,
                    height: 36,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                    "&:hover": {
                      bgcolor: BRAND_HOVER,
                      transform: "scale(1.05)"
                    },
                    transition: "all 0.2s ease-in-out"
                  }}
                >
                  <CameraAltIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </label>
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textAlign: "center" }}
            >
              {t(
                "upload-photo-hint",
                "Click the camera icon to upload a photo"
              )}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label={t("first-name", "First Name")}
                  placeholder={t("enter-first-name", "Enter first name")}
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                  disabled={isLoading}
                />
              )}
            />

            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label={t("last-name", "Last Name")}
                  placeholder={t("enter-last-name", "Enter last name")}
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                  disabled={isLoading}
                />
              )}
            />

            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label={t("email", "Email")}
                  placeholder={t("enter-email", "Enter email")}
                  type="email"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  disabled={isLoading}
                />
              )}
            />

            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
              <Controller
                name="phoneCountryCode"
                control={control}
                render={({ field }) => (
                  <FormControl
                    sx={{ flex: "0 0 160px", minWidth: 140 }}
                    variant="outlined"
                    error={!!errors.phoneCountryCode}
                    disabled={isLoading}
                    size="small"
                  >
                    <InputLabel id="phone-country-code-label">
                      {t("code", "Code")}
                    </InputLabel>
                    <Select
                      {...field}
                      value={field.value || getDefaultCountryCode()}
                      labelId="phone-country-code-label"
                      id="phone-country-code"
                      label={t("code", "Code")}
                      disabled={isLoading}
                    >
                      {Object.keys(PhoneCountryCodeMap).map((key) => (
                        <MenuItem key={key} value={key}>
                          {`${PhoneCountryCodeMap[key].flag} ${PhoneCountryCodeMap[key].code}`}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.phoneCountryCode && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, ml: 1.75 }}
                      >
                        {errors.phoneCountryCode.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />

              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("phone-number", "Phone Number")}
                    fullWidth
                    size="small"
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                    placeholder="782484464"
                    disabled={isLoading}
                  />
                )}
              />
            </Box>

            <Controller
              name="storeId"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  size="small"
                  label={t("store", "Store")}
                  error={!!errors.storeId}
                  helperText={errors.storeId?.message}
                  disabled={isLoading || isStoreAdmin}
                >
                  {storeList.map((store) => (
                    <MenuItem key={store.id} value={store.id}>
                      {store.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            <Controller
              name="role"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  size="small"
                  label={t("role", "Role")}
                  error={!!errors.role}
                  helperText={errors.role?.message}
                  disabled={isLoading || assignableRoles.length <= 1}
                >
                  {assignableRoles.map((role) => (
                    <MenuItem key={role} value={role}>
                      {formatUserRoleLabel(role, t, config?.businessType)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 2, mt: 2, flexShrink: 0 }}>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={isLoading}
              fullWidth
              sx={{
                textTransform: "none",
                borderRadius: 2,
                backgroundColor: BRAND_COLOR,
                "&:hover": { backgroundColor: BRAND_HOVER }
              }}
            >
              {t("add-employee", "Add Employee")}
            </LoadingButton>

            <Button
              type="button"
              variant="outlined"
              onClick={onClose}
              disabled={isLoading}
              fullWidth
              sx={{
                textTransform: "none",
                borderRadius: 2,
                backgroundColor: "white",
                color: BRAND_COLOR,
                border: `1px solid ${BRAND_COLOR}`,
                "&:hover": {
                  backgroundColor: BRAND_HOVER,
                  color: "white",
                  borderColor: BRAND_HOVER
                }
              }}
            >
              {t("cancel", "Cancel")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddEmployeeModal;
